import React from "react";
import Typography from "aldoo-ra/Typography";
import Checkbox from "aldoo-ra/Checkbox";
import DropDownSelect from "aldoo-ra/DropdownSelect";

/**
 * FieldSelector Component
 * Allows selection of one or more fields from content type definition
 */
const FieldSelector = ({
  value,
  onChange,
  fields,
  multiple = false,
  label,
  required = false,
  error,
  placeholder = "Select field(s)",
  variant = "admin"
}) => {
  // Format types display - show up to 3 types, then "+ N more"
  const formatTypes = (types) => {
    if (!types || types.length === 0) return '';
    if (types.length <= 3) return types.join(', ');
    return `${types.slice(0, 3).join(', ')} (+${types.length - 3} more)`;
  };

  // Handle single select
  const handleSingleChange = (selected) => {
    const selectedItem = Array.isArray(selected) ? selected[0] : selected;
    onChange(selectedItem?.value || '');
  };

  // Handle multi select
  const handleMultiChange = (fieldName, isSelected) => {
    const currentValue = Array.isArray(value) ? value : [];
    const newValue = isSelected
      ? [...currentValue, fieldName]
      : currentValue.filter(f => f !== fieldName);
    onChange(newValue);
  };

  if (multiple) {
    return (
      <div>
        {label && (
          <Typography variant="admin_label" className="mb-1">
            {label}
            {required && <span className="text-admin_error ml-1">*</span>}
          </Typography>
        )}

        <DropDownSelect
          options={fields.map(field => ({
            value: field.name,
            label: field.label || field.name,
            name: field.label || field.name,
            fieldType: field.type,
            description: field.description,
            usedByTypes: field.usedByTypes || []
          }))}
          value={Array.isArray(value) ? value.map(v => fields.find(f => f.name === v)).filter(Boolean) : []}
          onChange={(selected) => {
            const selectedValues = Array.isArray(selected) ? selected.map(s => s.value) : [];
            onChange(selectedValues);
          }}
          placeholder={placeholder}
          variant="admin"
          nameField="name"
          multiSelection={true}
          showSelectionTick={true}
          showCheckbox={false}
          searchEnabled={true}
          renderOption={(option) => (
            <div className="relative">
              <div className="flex items-center justify-between">
                <Typography variant="admin_body" className="text-sm font-bold m-0 pr-2">
                  {option.label}
                </Typography>
                <Typography variant="admin_body" className="text-admin_muted text-xs m-0 ml-auto">
                  {option.value}
                </Typography>
              </div>
              {(option.description || (option.usedByTypes && option.usedByTypes.length > 0)) && (
                <div className="flex items-center justify-between gap-4 mt-1">
                  <Typography variant="admin_body" className="text-admin_muted text-xs flex-1 m-0">
                    {option.description || ''}
                  </Typography>
                  {option.usedByTypes && option.usedByTypes.length > 0 && (
                    <Typography variant="admin_body" className="text-admin_muted text-xs flex-shrink-0 m-0">
                      Used by: {formatTypes(option.usedByTypes)}
                    </Typography>
                  )}
                </div>
              )}
            </div>
          )}
        />

        {error && (
          <Typography variant="admin_caption" className="text-admin_error mt-1">
            {error}
          </Typography>
        )}
      </div>
    );
  }

  // Single select with DropdownSelect
  return (
    <div>
      {label && (
        <Typography variant="admin_label" className="mb-1">
          {label}
          {required && <span className="text-admin_error ml-1">*</span>}
        </Typography>
      )}

      <DropDownSelect
        options={fields.map(field => ({
          value: field.name,
          label: field.label || field.name,
          name: field.label || field.name,
          fieldType: field.type,
          description: field.description,
          usedByTypes: field.usedByTypes || []
        }))}
        value={value ? [fields.find(f => f.name === value)] : []}
        onChange={handleSingleChange}
        placeholder={placeholder}
        variant="admin"
        nameField="name"
        searchEnabled={true}
        renderOption={(option) => (
          <div className="relative">
            <div className="flex items-center justify-between">
              <Typography variant="admin_body" className="text-sm font-bold m-0 pr-2">
                {option.label}
              </Typography>
              <Typography variant="admin_body" className="text-admin_muted text-xs m-0 ml-auto">
                {option.value}
              </Typography>
            </div>
            {(option.description || (option.usedByTypes && option.usedByTypes.length > 0)) && (
              <div className="flex items-center justify-between gap-4 mt-1">
                <Typography variant="admin_body" className="text-admin_muted text-xs flex-1 m-0">
                  {option.description || ''}
                </Typography>
                {option.usedByTypes && option.usedByTypes.length > 0 && (
                  <Typography variant="admin_body" className="text-admin_muted text-xs flex-shrink-0 m-0">
                    Used by: {formatTypes(option.usedByTypes)}
                  </Typography>
                )}
              </div>
            )}
          </div>
        )}
      />

      {error && (
        <Typography variant="admin_caption" className="text-admin_error mt-1">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default FieldSelector;
